import { useState } from 'react'
import * as AlertDialog from '@radix-ui/react-alert-dialog'
import {
  ChevronRight,
  ChevronDown,
  FolderOpen,
  X as XIcon,
  Pencil,
  Check,
  Loader2,
  Inbox,
  Sparkles,
} from 'lucide-react'
import {
  createTheme,
  type AutoThemeProposal,
  type FeedbackItem,
} from '../../lib/feedbackApi'

const TYPE_BADGE: Record<string, { bg: string; text: string; label: string }> = {
  bug: { bg: 'bg-red-500/10', text: 'text-red-600 dark:text-red-400', label: 'Bug' },
  feature: { bg: 'bg-blue-500/10', text: 'text-blue-600 dark:text-blue-400', label: 'Feature' },
  improvement: { bg: 'bg-green-500/10', text: 'text-green-600 dark:text-green-400', label: 'Improvement' },
  integration: { bg: 'bg-purple-500/10', text: 'text-purple-600 dark:text-purple-400', label: 'Integration' },
}

interface EditableProposal extends AutoThemeProposal {
  rejected: boolean
  removedIds: Set<number>
  editingName: boolean
  editedName: string
  expanded: boolean
}

interface AutoThemeReviewProps {
  proposals: AutoThemeProposal[]
  unclassifiableIds: number[]
  ungroupedItems: FeedbackItem[]
  totalUngrouped: number
  onDone: () => void
  onItemSelect: (id: number) => void
}

export function AutoThemeReview({
  proposals,
  unclassifiableIds,
  ungroupedItems,
  totalUngrouped,
  onDone,
  onItemSelect,
}: AutoThemeReviewProps) {
  const [themes, setThemes] = useState<EditableProposal[]>(() =>
    proposals.map((p) => ({
      ...p,
      rejected: false,
      removedIds: new Set<number>(),
      editingName: false,
      editedName: p.name,
      expanded: true,
    }))
  )
  const [applying, setApplying] = useState(false)
  const [showDiscard, setShowDiscard] = useState(false)
  const [result, setResult] = useState<{ created: number; total: number } | null>(null)

  const activeCount = themes.filter((t) => !t.rejected).length

  // Theme manipulation functions

  function rejectTheme(index: number) {
    setThemes((prev) =>
      prev.map((t, i) => (i === index ? { ...t, rejected: true } : t))
    )
  }

  function restoreTheme(index: number) {
    setThemes((prev) =>
      prev.map((t, i) => (i === index ? { ...t, rejected: false } : t))
    )
  }

  function removeItem(themeIndex: number, feedbackId: number) {
    setThemes((prev) =>
      prev.map((t, i) => {
        if (i !== themeIndex) return t
        const newRemoved = new Set(t.removedIds)
        newRemoved.add(feedbackId)
        // Auto-reject if all items removed
        const remaining = t.feedback_ids.filter((id) => !newRemoved.has(id))
        return {
          ...t,
          removedIds: newRemoved,
          rejected: remaining.length === 0 ? true : t.rejected,
        }
      })
    )
  }

  function startRename(index: number) {
    setThemes((prev) =>
      prev.map((t, i) => (i === index ? { ...t, editingName: true } : t))
    )
  }

  function confirmRename(index: number) {
    setThemes((prev) =>
      prev.map((t, i) =>
        i === index ? { ...t, name: t.editedName, editingName: false } : t
      )
    )
  }

  function updateEditedName(index: number, value: string) {
    setThemes((prev) =>
      prev.map((t, i) => (i === index ? { ...t, editedName: value } : t))
    )
  }

  function cancelRename(index: number) {
    setThemes((prev) =>
      prev.map((t, i) =>
        i === index ? { ...t, editedName: t.name, editingName: false } : t
      )
    )
  }

  function toggleExpand(index: number) {
    setThemes((prev) =>
      prev.map((t, i) => (i === index ? { ...t, expanded: !t.expanded } : t))
    )
  }

  async function handleAcceptAll() {
    setApplying(true)
    const activeThemes = themes.filter((t) => !t.rejected)
    const results = await Promise.allSettled(
      activeThemes.map(async (theme) => {
        const activeIds = theme.feedback_ids.filter(
          (id) => !theme.removedIds.has(id)
        )
        if (activeIds.length === 0) return null
        return createTheme({
          name: theme.editedName || theme.name,
          description: theme.description || null,
          feedback_ids: activeIds,
        })
      })
    )

    const created = results.filter(
      (r) => r.status === 'fulfilled' && r.value !== null
    ).length
    const total = activeThemes.filter(
      (t) =>
        t.feedback_ids.filter((id) => !t.removedIds.has(id)).length > 0
    ).length

    if (created === total) {
      // All succeeded -- return to normal view
      onDone()
    } else {
      // Partial success -- show result
      setResult({ created, total })
      setApplying(false)
    }
  }

  return (
    <div className="space-y-4">
      {/* Banner */}
      <div className="p-4 rounded-lg bg-primary/10 border border-primary/20 flex items-center justify-between mb-6">
        <div className="flex items-center gap-2">
          <Sparkles className="w-5 h-5 text-primary" />
          <span className="text-sm font-medium">
            AI proposed {activeCount} themes from {totalUngrouped} ungrouped
            items
          </span>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowDiscard(true)}
            className="px-3 py-1.5 text-sm border border-destructive text-destructive rounded-lg hover:bg-destructive/10 transition-colors"
            aria-label="Discard all proposed themes"
          >
            Discard All
          </button>
          <button
            onClick={handleAcceptAll}
            disabled={applying || activeCount === 0}
            className="px-3 py-1.5 text-sm bg-primary text-primary-foreground rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50 flex items-center gap-2"
            aria-label="Accept all proposed themes"
          >
            {applying ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" /> Applying...
              </>
            ) : (
              'Accept All'
            )}
          </button>
        </div>
      </div>

      {/* Partial success result */}
      {result && (
        <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/20 text-sm text-amber-600 dark:text-amber-400 mb-4">
          Created {result.created} of {result.total} themes. Some themes could
          not be created.
          <button onClick={onDone} className="ml-2 underline">
            Back to Themes
          </button>
        </div>
      )}

      {/* Proposed Theme Cards */}
      {themes
        .map((theme, originalIndex) => ({ theme, originalIndex }))
        .filter(({ theme }) => !theme.rejected)
        .map(({ theme, originalIndex }) => (
          <div
            key={originalIndex}
            className="border border-border rounded-lg overflow-hidden"
          >
            {/* Header */}
            <div className="flex items-center gap-2 px-4 py-3 bg-secondary/30">
              <button
                onClick={() => toggleExpand(originalIndex)}
                className="p-0.5"
              >
                {theme.expanded ? (
                  <ChevronDown className="w-4 h-4" />
                ) : (
                  <ChevronRight className="w-4 h-4" />
                )}
              </button>
              <FolderOpen className="w-4 h-4 text-muted-foreground" />

              {/* Theme name (inline edit or display) */}
              {theme.editingName ? (
                <div className="flex items-center gap-1 flex-1">
                  <input
                    value={theme.editedName}
                    onChange={(e) =>
                      updateEditedName(originalIndex, e.target.value)
                    }
                    className="flex-1 bg-background border border-border rounded px-2 py-0.5 text-sm"
                    autoFocus
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') confirmRename(originalIndex)
                      if (e.key === 'Escape') cancelRename(originalIndex)
                    }}
                  />
                  <button
                    onClick={() => confirmRename(originalIndex)}
                    className="p-1 hover:bg-secondary rounded"
                  >
                    <Check className="w-3.5 h-3.5" />
                  </button>
                  <button
                    onClick={() => cancelRename(originalIndex)}
                    className="p-1 hover:bg-secondary rounded"
                  >
                    <XIcon className="w-3.5 h-3.5" />
                  </button>
                </div>
              ) : (
                <span className="font-semibold text-sm flex-1">
                  {theme.editedName || theme.name}
                </span>
              )}

              <span className="text-xs text-muted-foreground">
                {
                  theme.feedback_ids.filter((id) => !theme.removedIds.has(id))
                    .length
                }{' '}
                items
              </span>

              {/* Controls */}
              {!theme.editingName && (
                <button
                  onClick={() => startRename(originalIndex)}
                  className="p-1 hover:bg-secondary rounded"
                  title="Rename theme"
                >
                  <Pencil className="w-3.5 h-3.5 text-muted-foreground" />
                </button>
              )}
              <button
                onClick={() => rejectTheme(originalIndex)}
                className="p-1 hover:bg-red-500/10 rounded"
                title="Reject entire group"
              >
                <XIcon className="w-3.5 h-3.5 text-destructive" />
              </button>
            </div>

            {/* Items */}
            {theme.expanded && (
              <div className="divide-y divide-border">
                {theme.feedback_ids
                  .filter((id) => !theme.removedIds.has(id))
                  .map((id) => {
                    const item = ungroupedItems.find((u) => u.id === id)
                    return item ? (
                      <div
                        key={id}
                        className="flex items-center gap-3 px-4 py-2 text-sm"
                      >
                        <button
                          onClick={() => onItemSelect(id)}
                          className="flex-1 text-left hover:text-primary truncate"
                        >
                          {item.title}
                        </button>
                        {item.type && TYPE_BADGE[item.type] && (
                          <span
                            className={`text-xs px-1.5 py-0.5 rounded ${TYPE_BADGE[item.type].bg} ${TYPE_BADGE[item.type].text}`}
                          >
                            {TYPE_BADGE[item.type].label}
                          </span>
                        )}
                        <button
                          onClick={() => removeItem(originalIndex, id)}
                          className="p-1 hover:bg-red-500/10 rounded"
                          title="Remove from this group"
                        >
                          <XIcon className="w-3.5 h-3.5 text-muted-foreground hover:text-destructive" />
                        </button>
                      </div>
                    ) : (
                      <div
                        key={id}
                        className="px-4 py-2 text-sm text-muted-foreground"
                      >
                        Feedback #{id}
                      </div>
                    )
                  })}
              </div>
            )}
          </div>
        ))}

      {/* Rejected themes */}
      {themes
        .map((theme, originalIndex) => ({ theme, originalIndex }))
        .filter(({ theme }) => theme.rejected)
        .map(({ theme, originalIndex }) => (
          <div
            key={originalIndex}
            className="border border-border rounded-lg px-4 py-3 opacity-50 flex items-center gap-2"
          >
            <FolderOpen className="w-4 h-4 text-muted-foreground" />
            <span className="text-sm line-through flex-1">{theme.name}</span>
            <button
              onClick={() => restoreTheme(originalIndex)}
              className="text-xs text-primary hover:underline"
            >
              Restore
            </button>
          </div>
        ))}

      {/* Unclassifiable items section */}
      {unclassifiableIds.length > 0 && (
        <div className="border border-border rounded-lg p-4 bg-secondary/10">
          <div className="flex items-center gap-2 mb-2">
            <Inbox className="w-4 h-4 text-muted-foreground" />
            <span className="text-sm font-semibold">Could not classify</span>
            <span className="text-xs text-muted-foreground">
              {unclassifiableIds.length} items
            </span>
          </div>
          <p className="text-xs text-muted-foreground">
            These items were too dissimilar to group together.
          </p>
        </div>
      )}

      {/* Discard confirmation dialog */}
      <AlertDialog.Root open={showDiscard} onOpenChange={setShowDiscard}>
        <AlertDialog.Portal>
          <AlertDialog.Overlay className="fixed inset-0 bg-black/50 z-50" />
          <AlertDialog.Content className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-card border border-border rounded-lg p-6 w-full max-w-md z-50 shadow-lg">
            <AlertDialog.Title className="text-lg font-semibold mb-2">
              Discard AI proposals?
            </AlertDialog.Title>
            <AlertDialog.Description className="text-muted-foreground mb-6">
              All proposed themes will be discarded. Ungrouped items will remain
              unchanged.
            </AlertDialog.Description>
            <div className="flex justify-end gap-3">
              <AlertDialog.Cancel asChild>
                <button
                  type="button"
                  className="px-4 py-2 rounded-lg bg-secondary hover:bg-secondary/80 transition-colors"
                >
                  Cancel
                </button>
              </AlertDialog.Cancel>
              <button
                type="button"
                onClick={() => {
                  setShowDiscard(false)
                  onDone()
                }}
                className="px-4 py-2 rounded-lg bg-destructive text-destructive-foreground hover:bg-destructive/90 transition-colors"
              >
                Discard
              </button>
            </div>
          </AlertDialog.Content>
        </AlertDialog.Portal>
      </AlertDialog.Root>
    </div>
  )
}
